<script lang="ts">
  import type { InjectionKey, ComputedRef } from 'vue'

  export interface RadioCardGroupContext {
    modelValue: ComputedRef<string>
    handleChange: (value: string) => void
    disabled: ComputedRef<boolean>
  }

  export const RADIO_CARD_GROUP_KEY: InjectionKey<RadioCardGroupContext> =
    Symbol('radio-card-group')
</script>

<script setup lang="ts">
  import { computed, provide, type HTMLAttributes } from 'vue'
  import { RadioGroupRoot } from 'reka-ui'
  import { cn } from '../../../lib/utils'

  /**
   * Form-agnostic radio-card group. Children are `<RadioCardGroupItem>`
   * components rather than a flat `items` array, so each option can host
   * its own conditional content via the item's `#footer` slot (e.g. a
   * picker revealed when that option is selected).
   *
   * Extracted from the old `FormRadioGroup` so visuals stay identical; the
   * value / change / blur wiring is passed in via props / emits instead of
   * read off a tanstack field. Drop it into a `FormField` default slot with
   * `input-group="false"` to get optional label + error chrome.
   */
  const props = defineProps<{
    modelValue?: string
    disabled?: boolean
    orientation?: 'horizontal' | 'vertical'
    dataTestid?: string
    ariaInvalid?: boolean
    class?: HTMLAttributes['class']
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'blur'): void
  }>()

  const orientation = computed(() => props.orientation ?? 'vertical')
  const modelValue = computed(() => props.modelValue ?? '')
  const disabled = computed(() => !!props.disabled)

  function handleChange(value: string) {
    emit('update:modelValue', value)
  }

  provide(RADIO_CARD_GROUP_KEY, { modelValue, handleChange, disabled })
</script>

<template>
  <RadioGroupRoot
    :model-value="modelValue"
    :disabled="disabled"
    :orientation="orientation"
    :data-testid="dataTestid"
    :aria-invalid="ariaInvalid || undefined"
    :class="
      cn(
        'flex w-full',
        orientation === 'horizontal' ? 'flex-row flex-wrap gap-3' : 'flex-col',
        props.class
      )
    "
    @update:model-value="(v) => handleChange((v as string | null) ?? '')"
  >
    <slot />
  </RadioGroupRoot>
</template>
